import { useState } from 'react';
import { useWorkflowAutomation } from '@/hooks/useWorkflowAutomation';
import { useReviewTasks } from '@/hooks/useReviewTasks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { 
  Play, 
  Plus, 
  Settings, 
  Zap, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Activity,
  AlertTriangle,
  UserCheck,
  FileText,
  Mail,
  Bell
} from 'lucide-react';

export function ReviewWorkflowManager() {
  const { 
    workflowTemplates, 
    workflowExecutions, 
    workflowStats, 
    isLoading, 
    createWorkflow, 
    toggleWorkflowStatus,
    executeWorkflow,
    isCreating 
  } = useWorkflowAutomation();

  const { tasks: reviewTasks } = useReviewTasks();
  const { toast } = useToast();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [triggerType, setTriggerType] = useState('bill_requires_review');
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const [escalationDays, setEscalationDays] = useState('3');
  const [priorityThreshold, setPriorityThreshold] = useState('high');

  const reviewTriggerTypes = [
    { value: 'bill_requires_review', label: 'Bill Requires Review', description: 'Triggered when a bill needs manual review' },
    { value: 'low_confidence_extraction', label: 'Low Confidence Extraction', description: 'Triggered when AI confidence is below threshold' },
    { value: 'validation_failed', label: 'Validation Failed', description: 'Triggered when validation rules fail' },
    { value: 'review_overdue', label: 'Review Overdue', description: 'Triggered when review tasks are overdue' },
    { value: 'escalation_required', label: 'Escalation Required', description: 'Triggered when manual escalation is needed' }
  ];

  const reviewWorkflowTemplates = [
    {
      name: 'Auto-Assign High Priority Reviews',
      description: 'Automatically assign high-priority bills to senior reviewers',
      trigger: 'bill_requires_review',
      actions: ['auto_assign', 'send_notification'],
      isActive: true
    },
    {
      name: 'Low Confidence Alert',
      description: 'Send alerts when AI confidence drops below 80%',
      trigger: 'low_confidence_extraction',
      actions: ['create_review_task', 'notify_supervisor'],
      isActive: true
    },
    {
      name: 'Review Escalation',
      description: 'Escalate reviews that remain unassigned for 24 hours',
      trigger: 'review_overdue',
      actions: ['escalate_to_manager', 'urgent_notification'],
      isActive: false
    }
  ];

  const handleCreateReviewWorkflow = () => {
    const actions = [];
    
    if (autoAssignEnabled) {
      actions.push({
        type: 'auto_assign_reviewer',
        config: {
          priority_threshold: priorityThreshold,
          assignment_strategy: 'load_balanced'
        }
      });
    }

    if (notificationEnabled) {
      actions.push({
        type: 'send_notification',
        config: {
          channels: ['email', 'in_app'],
          template: 'review_required'
        }
      });
    }

    if (escalationDays) {
      actions.push({
        type: 'schedule_escalation',
        config: {
          delay_days: parseInt(escalationDays),
          escalation_level: 'supervisor'
        }
      });
    }

    const triggerConfig = {
      priority_filter: priorityThreshold !== 'all' ? priorityThreshold : null,
      confidence_threshold: 0.8,
      auto_create_task: true
    };

    createWorkflow({
      name: workflowName,
      description: workflowDescription,
      trigger_type: triggerType,
      trigger_config: triggerConfig,
      actions,
      conditions: {
        enabled: true,
        review_required: true
      }
    });

    resetForm();
    setShowCreateDialog(false);
    
    toast({
      title: "Review workflow created",
      description: "Your review workflow has been created and is now active.",
    });
  };

  const resetForm = () => {
    setWorkflowName('');
    setWorkflowDescription('');
    setTriggerType('bill_requires_review');
    setAutoAssignEnabled(false);
    setNotificationEnabled(true);
    setEscalationDays('3');
    setPriorityThreshold('high');
  };

  const getActionIcons = (actions: any[]) => {
    const iconMap = {
      auto_assign_reviewer: UserCheck,
      send_notification: Mail,
      schedule_escalation: AlertTriangle,
      create_review_task: FileText
    };

    return actions.map((action, index) => {
      const IconComponent = iconMap[action.type as keyof typeof iconMap] || Settings;
      return <IconComponent key={index} className="h-4 w-4" />;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/4"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const reviewWorkflows = workflowTemplates.filter(template => 
    template.trigger_type.includes('review') || 
    template.trigger_type.includes('confidence') ||
    template.trigger_type.includes('validation')
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Review Workflow Manager</h2>
          <p className="text-muted-foreground">Automate your manual review processes and notifications</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Review Workflow
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Review Workflow</DialogTitle>
              <DialogDescription>
                Configure an automated workflow for manual review processes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="workflowName">Workflow Name</Label>
                <Input
                  id="workflowName"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="Auto-assign high priority reviews"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="workflowDescription">Description</Label>
                <Textarea
                  id="workflowDescription"
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  placeholder="Describe what this workflow does..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="triggerType">Trigger Event</Label>
                <Select value={triggerType} onValueChange={setTriggerType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reviewTriggerTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-xs text-muted-foreground">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />
              
              <div className="space-y-4">
                <h4 className="font-medium">Workflow Actions</h4>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Auto-assign Reviewers</Label>
                    <p className="text-xs text-muted-foreground">Automatically assign tasks to available reviewers</p>
                  </div>
                  <Switch
                    checked={autoAssignEnabled}
                    onCheckedChange={setAutoAssignEnabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Send Notifications</Label>
                    <p className="text-xs text-muted-foreground">Notify relevant team members</p>
                  </div>
                  <Switch
                    checked={notificationEnabled}
                    onCheckedChange={setNotificationEnabled}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="priorityThreshold">Priority Filter</Label>
                    <Select value={priorityThreshold} onValueChange={setPriorityThreshold}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="high">High Priority Only</SelectItem>
                        <SelectItem value="medium">Medium & High</SelectItem>
                        <SelectItem value="low">Low Priority Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="escalationDays">Escalation (Days)</Label>
                    <Input
                      id="escalationDays"
                      type="number"
                      value={escalationDays}
                      onChange={(e) => setEscalationDays(e.target.value)}
                      min="1"
                      max="30"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateReviewWorkflow} disabled={isCreating || !workflowName}>
                {isCreating ? "Creating..." : "Create Workflow"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Review Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Workflows</CardTitle>
            <Zap className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reviewWorkflows.filter(w => w.is_active).length}</div>
            <p className="text-xs text-muted-foreground">running workflows</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
            <Clock className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reviewTasks.filter(t => t.status === 'pending').length}</div>
            <p className="text-xs text-muted-foreground">awaiting action</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Auto-assigned</CardTitle>
            <UserCheck className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reviewTasks.filter(t => t.assigned_to).length}</div>
            <p className="text-xs text-muted-foreground">assigned tasks</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{workflowStats.avgSuccessRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">automation success</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active">Active Workflows</TabsTrigger>
          <TabsTrigger value="templates">Workflow Templates</TabsTrigger>
          <TabsTrigger value="executions">Recent Executions</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Active Review Workflows</CardTitle>
              <CardDescription>
                Currently running automated review workflows
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Actions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviewWorkflows.map((workflow) => (
                    <TableRow key={workflow.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{workflow.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {workflow.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {workflow.trigger_type.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-1">
                          {getActionIcons(workflow.actions).slice(0, 3)}
                          {workflow.actions.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{workflow.actions.length - 3}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={workflow.is_active}
                          onCheckedChange={(checked) =>
                            toggleWorkflowStatus({ id: workflow.id, isActive: checked })
                          }
                        />
                      </TableCell>
                      <TableCell>{workflow.usage_count} runs</TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => executeWorkflow({ templateId: workflow.id })}
                        >
                          <Play className="mr-1 h-3 w-3" />
                          Test
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {reviewWorkflows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No review workflows found. Create your first workflow to automate your review process.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Workflow Templates</CardTitle>
              <CardDescription>
                Pre-configured workflow templates for common review scenarios
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {reviewWorkflowTemplates.map((template, index) => (
                  <Card key={index} className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">{template.name}</CardTitle>
                        <Badge variant={template.isActive ? "default" : "secondary"}>
                          {template.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <CardDescription className="text-xs">
                        {template.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="text-xs">
                          <span className="font-medium">Trigger:</span> {template.trigger.replace(/_/g, ' ')}
                        </div>
                        <div className="text-xs">
                          <span className="font-medium">Actions:</span> {template.actions.join(', ')}
                        </div>
                        <Button size="sm" variant="outline" className="w-full">
                          Use Template
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="executions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Workflow Executions</CardTitle>
              <CardDescription>
                Latest executions of your review workflows
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Trigger Data</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflowExecutions.slice(0, 10).map((execution) => {
                    const duration = execution.completed_at 
                      ? Math.round((new Date(execution.completed_at).getTime() - new Date(execution.started_at).getTime()) / 1000)
                      : null;
                    
                    return (
                      <TableRow key={execution.id}>
                        <TableCell>
                          <div className="font-medium">
                            {execution.workflow_templates?.name || 'Unknown Workflow'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {execution.status === 'completed' && <CheckCircle className="h-4 w-4 text-success" />}
                            {execution.status === 'failed' && <XCircle className="h-4 w-4 text-destructive" />}
                            {execution.status === 'running' && <Activity className="h-4 w-4 text-primary" />}
                            <Badge variant={execution.status === 'completed' ? 'default' : execution.status === 'failed' ? 'destructive' : 'secondary'}>
                              {execution.status}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-muted-foreground">
                            {execution.trigger_data?.bill_id ? `Bill ID: ${execution.trigger_data.bill_id.slice(0, 8)}...` : 'No data'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(execution.started_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {duration ? `${duration}s` : 'Running...'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {workflowExecutions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No workflow executions found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}